import { useMemo } from 'react';
import { create } from 'zustand';
import Collection from '@model-core/core/Collection';
import CompanyModel from '@/models/entities/CompanyModel';
import MemberModel from '@/models/entities/MemberModel';
import TeamMemberModel from '@/models/entities/TeamMemberModel';
import { DEFAULT_TIMEZONE } from '@/lib/companyTime';
import { TEAM_MEMBER_STATUS, TEAM_ROLE } from '@/models/references/references';
import { loadReferences, resolveReference } from '@/stores/useReferenceOptions';
import type { AuthUser } from '@/security/session';

/** Dernière société choisie, retrouvée au prochain chargement */
export const COMPANY_STORAGE_KEY = 'teiji.company';

/** Société du sélecteur de la sidebar */
export interface CompanyOption {
  id: number;
  name: string;
  /** Fuseau IANA de la société : jour courant des feuilles d'heures (todayIn) */
  timezone: string;
}

/** Rattachement de l'utilisateur connecté à la société sélectionnée, chargé à sa sélection */
export interface Membership {
  /** Rôle de l'utilisateur dans la société (DIRECTOR…), null s'il n'en est pas employé */
  companyRole: string | null;
  /** Son rattachement à la société (Member : feuilles d'heures), null s'il n'en est pas employé */
  memberId: number | null;
  /** Équipes de la société dont il est le manager actif (Validation de ses équipes) */
  managedTeamIds: number[];
}

/** Société sélectionnée, avec le rattachement de l'utilisateur */
export type CurrentCompany = CompanyOption & Membership;

interface CompanyStore {
  /** Sociétés proposées par le sélecteur de la sidebar (celles dont l'utilisateur est employé), triées par nom */
  companies: CompanyOption[];
  /** Société sélectionnée : les écrans par société (Employés…) s'y limitent */
  currentCompanyId: number | null;
  /**
   * Rattachement à la société sélectionnée (companyId : la société pour laquelle il a été chargé) ; pendant un
   * changement de société, celui de la précédente jusqu'au chargement du nouveau
   */
  membership: (Membership & { companyId: number }) | null;
  /** Sociétés chargées, et le rattachement à la société sélectionnée : les droits par société sont connus */
  loaded: boolean;
  /**
   * Charge les sociétés dont l'utilisateur connecté est employé (admin compris), puis son rattachement à la société
   * sélectionnée. Un chargement déjà en cours pour le même utilisateur est réutilisé (pas de doublon, ex. effet rejoué
   * par StrictMode en dev)
   */
  load: (user: AuthUser) => Promise<void>;
  /** Change de société : son rattachement (rôle, équipes managées) est rechargé */
  select: (companyId: number) => Promise<void>;
}

export const useCompanyStore = create<CompanyStore>(() => ({
  companies: [],
  currentCompanyId: null,
  membership: null,
  loaded: false,

  load: (user) => {
    if (loadInFlight?.urn === user.urn) return loadInFlight.promise;
    const promise = doLoad(user).finally(() => {
      if (loadInFlight?.promise === promise) loadInFlight = null;
    });
    loadInFlight = { urn: user.urn, promise };
    lastLoadedUrn = user.urn;
    return promise;
  },

  select: async (companyId) => {
    try {
      localStorage.setItem(COMPANY_STORAGE_KEY, String(companyId));
    } catch {
      // stockage indisponible : le choix vaut pour cet onglet seulement
    }
    if (useCompanyStore.getState().currentCompanyId === companyId) return;
    // Rattachement précédent gardé le temps du chargement : menu et accès aux pages restent stables (pas de page démontée)
    useCompanyStore.setState({ currentCompanyId: companyId, loaded: false });
    if (lastLoadedUrn) await applyMembership(lastLoadedUrn, companyId);
  },
}));

/** Chargement en cours, avec l'utilisateur (son URN) pour qui il a été lancé */
let loadInFlight: { urn: string; promise: Promise<void> } | null = null;
/** Utilisateur du dernier chargement lancé : seul son résultat est gardé */
let lastLoadedUrn: string | null = null;

const userIdOf = (urn: string) => urn.split(':').pop() ?? '';

async function doLoad(user: AuthUser): Promise<void> {
  // Rien de l'utilisateur précédent pendant le chargement (changement de compte)
  useCompanyStore.setState({ companies: [], currentCompanyId: null, membership: null, loaded: false });
  // API injoignable ou refus : aucune société proposée, les écrans par société l'indiquent
  const companies = await loadCompaniesOf(userIdOf(user.urn)).catch(() => []);

  // Un autre utilisateur s'est connecté entre-temps : son propre chargement fait foi
  if (lastLoadedUrn !== user.urn) return;
  const stored = readStoredCompanyId();
  const current = companies.find((company) => company.id === stored) ?? companies[0] ?? null;
  useCompanyStore.setState({ companies, currentCompanyId: current?.id ?? null, loaded: !current });
  if (current) await applyMembership(user.urn, current.id);
}

/** Charge le rattachement à une société ; gardé seulement si l'utilisateur et la société n'ont pas changé entre-temps */
async function applyMembership(urn: string, companyId: number): Promise<void> {
  const membership = await loadMembership(userIdOf(urn), companyId);
  if (lastLoadedUrn !== urn || useCompanyStore.getState().currentCompanyId !== companyId) return;
  useCompanyStore.setState({ membership: { ...membership, companyId }, loaded: true });
}

/** Société sélectionnée et rattachement de l'utilisateur ; null tant que ce rattachement n'est pas chargé */
export function useCurrentCompany(): CurrentCompany | null {
  const company = useCompanyStore(
    (state) => state.companies.find((company) => company.id === state.currentCompanyId) ?? null,
  );
  const membership = useCompanyStore((state) => state.membership);

  return useMemo(
    () =>
      company && membership?.companyId === company.id
        ? {
            ...company,
            companyRole: membership.companyRole,
            memberId: membership.memberId,
            managedTeamIds: membership.managedTeamIds,
          }
        : null,
    [company, membership],
  );
}

/** Fuseau de la société sélectionnée : heure légale des lignes saisies et affichées */
export function useCompanyTimezone(): string {
  return useCompanyStore(
    (state) => state.companies.find((company) => company.id === state.currentCompanyId)?.timezone ?? DEFAULT_TIMEZONE,
  );
}

/** Sociétés dont l'utilisateur est employé (GET /companies?members.user=…), triées par nom */
async function loadCompaniesOf(userId: string): Promise<CompanyOption[]> {
  const { collection } = await new Collection(CompanyModel).setFilter('members.user', userId).setSort('name').list();
  return collection.models.map((company) => ({
    id: Number(company.id),
    name: company.name,
    timezone: company.timezone || DEFAULT_TIMEZONE,
  }));
}

const NO_MEMBERSHIP: Membership = { companyRole: null, memberId: null, managedTeamIds: [] };

/**
 * Rattachement de l'utilisateur à une société (GET /members?company=…&user=…) et équipes qu'il y manage ; sans lui
 * (pas employé, erreur), aucun droit dans la société
 */
async function loadMembership(userId: string, companyId: number): Promise<Membership> {
  try {
    // companyRole arrive réduit à son id : sa référence (droits du Dirigeant) vient de la liste des rôles
    const [{ collection }] = await Promise.all([
      new Collection(MemberModel).setFilter('company', String(companyId)).setFilter('user', userId).list(),
      loadReferences('companyRoles'),
    ]);
    const member = collection.models[0];
    if (!member) return NO_MEMBERSHIP;

    const memberId = Number(member.id);
    return {
      companyRole: member.companyRoleReference || null,
      memberId,
      managedTeamIds: (await loadManagedTeams(memberId)).map((teamMember) => teamMember.teamId),
    };
  } catch {
    return NO_MEMBERSHIP;
  }
}

/**
 * Appartenances actives où le rattachement est manager (une équipe chacune) ; sans elles (erreur), pas de vue manager.
 * L'API restreint de même ses droits sur les feuilles (groupe team_manager, contexte team)
 */
async function loadManagedTeams(memberId: number): Promise<TeamMemberModel[]> {
  try {
    const [manager, active] = await Promise.all([
      resolveReference('teamRoles', TEAM_ROLE.MANAGER),
      resolveReference('teamMemberStatuses', TEAM_MEMBER_STATUS.ACTIVE),
    ]);
    const { collection } = await new Collection(TeamMemberModel)
      .setFilter('member', String(memberId))
      .setFilter('teamRole', String(manager.id))
      .setFilter('teamMemberStatus', String(active.id))
      .list();
    return collection.models;
  } catch {
    return [];
  }
}

function readStoredCompanyId(): number | null {
  try {
    const value = Number(localStorage.getItem(COMPANY_STORAGE_KEY));
    return Number.isInteger(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}
